import { APIGet, APIPost, APIPut, APIDelete, APIUrl, AdminType, StatusCode } from "api/protocols";
import { CancelToken } from "axios";
import { IFacility } from "redux/reducers/models/facility";
import { IDepartments } from "redux/reducers/models/product";

interface IGetFacilityRes {
  status: StatusCode;
  data: IFacility[];
  message: string;
}

interface IPutFacilityRes {
  status: number;
  data: IFacility;
  message: string;
}

export type TGetFacility = {
  id?: string | number;
  "client-facilities"?: boolean;
  client?: boolean;
};
type TPutFacility = {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  province_id?: string | number;
  country_id?: string | number;
  postal?: string;
  phone?: string;
  merchant_code?: string;
  logo_source?: string;
  google_analytics_code?: string;
  facebook_pixel_code?: string;
};

interface IAccountingRef {
  id?: number;
  title?: string;
  reference_number: string;
}

interface IPutCurrency {
  id: number;
  default: boolean;
}

// GET Facility
export async function GetFacility(
  params: TGetFacility,
  useGlobalLoader: boolean,
  token?: CancelToken,
): Promise<IGetFacilityRes> {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility"), params, useGlobalLoader, token);
}

// PUT Facility
export async function PutFacility(params: TPutFacility, useGlobalLoader: boolean): Promise<IPutFacilityRes> {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility"), params, useGlobalLoader);
}

//POST Facility Logo Image
export async function PostFacilityLogo(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/image"), params, useGlobalLoader);
}

export async function GetPaymentOptions(
  params?: Record<string, unknown>,
  useGlobalLoader?: boolean,
  token?: CancelToken,
) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/facility/payment-option"),
    params,
    (useGlobalLoader = false),
    token,
  );
}

//GET Departments
export async function GetDepartments(useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet(
    APIUrl(AdminType.FacilityAdmin, "/facility/department?extended=true"),
    null,
    useGlobalLoader,
    token,
  );
}

//POST Departments
export async function PostDepartments(params: IDepartments, useGlobalLoader?: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/department"), params, useGlobalLoader);
}

//PUT Departments
export async function PutDepartments(params: IDepartments, useGlobalLoader?: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/department"), params, useGlobalLoader);
}

//DELETE Departments
export async function DeleteDepartments(params: IDepartments, useGlobalLoader?: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/facility/department"), params, useGlobalLoader);
}

export async function GetPayout(useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/payout"), null, useGlobalLoader);
}

export async function GetTransactions(params: string, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/payout/transaction"), params, useGlobalLoader);
}

// GET Accounting Reference
export async function GetAccountingReference(params: string, useGlobalLoader?: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/accounting?") + params, null, useGlobalLoader);
}

// POST Accounting Reference
export async function PostAccountingReference(params: IAccountingRef, useGlobalLoader?: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/facility/accounting"), params, useGlobalLoader);
}

// PUT Accounting Reference
export async function PutAccountingReference(params: IAccountingRef, useGlobalLoader?: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/accounting"), params, useGlobalLoader);
}

// DELETE Accounting Reference
export async function DeleteAccountingReference(params: IAccountingRef, useGlobalLoader?: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/facility/accounting"), params, useGlobalLoader);
}

//GET Accounts
export async function GetAccounts(useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/client/account"), useGlobalLoader);
}

//GET Sales Channels
//Retrieve a list of sales channels for the facility
export async function GetSalesChannels(params: any, useGlobalLoader: boolean) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/sales-channel"), useGlobalLoader);
}

//PUT Update Sales Channel
//Adds or removes a product from sales channel
export async function UpdateSalesChannel(params: any, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/sales-channel/product"), params, useGlobalLoader);
}

export async function GetFacilityCurrencies(
  params: { id?: number },
  useGlobalLoader: boolean,
  cancelToken?: CancelToken,
) {
  return await APIGet(APIUrl(AdminType.FacilityAdmin, "/facility/currency"), params, useGlobalLoader, cancelToken);
}

export async function PutFacilityCurrency(params: IPutCurrency, useGlobalLoader: boolean, cancelToken?: CancelToken) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/facility/currency"), params, useGlobalLoader, cancelToken);
}
